import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle2 } from 'lucide-react';
import filesApi from '../../../services/apis/files';
import { Progress, addToast } from '@heroui/react';
import { v4 as uuidv4 } from 'uuid';
import { getMimeTypeFromExtension, getAcceptStringForInput, AcceptedFileType } from './utils';

export type UploadStage = 'idle' | 'getting-url' | 'uploading' | 'saving-metadata' | 'completed' | 'error';

export interface FileUploadProps {
  file: File | null;
  onChange: (file: File | null) => void;
  acceptedTypes?: AcceptedFileType[];
  maxSize?: number; // in MB
  isMediaLibrary?: boolean;
  onFileUploaded?: (fileMetadata: {
    id: string;
    name: string;
    url: string;
    size: number;
    type: string;
    provider: string;
    createdAt: string;
  }) => void;
  onError?: (error: string) => void;
  onProgressChange?: (progress: number) => void;
  onStageChange?: (stage: UploadStage) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  file,
  onChange,
  acceptedTypes = [AcceptedFileType.PDF],
  maxSize = 10,
  isMediaLibrary = false,
  onFileUploaded,
  onError,
  onProgressChange,
  onStageChange,
}) => {
  const { t } = useTranslation('admin');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<UploadStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateProgress = (progress: number) => {
    setUploadProgress(progress);
    onProgressChange?.(progress);
  };

  const updateStage = (stage: UploadStage) => {
    setUploadStage(stage);
    onStageChange?.(stage);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const uploadToS3 = (file: File, presignedUrl: string, onProgress: (progress: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          // Upload progress is 0-80% (rest is for metadata saving)
          onProgress(Math.round((progress * 80) / 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          const errorMessage =
            xhr.status === 0
              ? 'CORS error: Check S3 bucket CORS configuration. The bucket must allow your origin and PUT method.'
              : `Upload failed with status ${xhr.status}`;
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => {
        // Check if it's a CORS error
        if (xhr.status === 0 && xhr.readyState === 4) {
          reject(
            new Error(
              'CORS error: The S3 bucket must have CORS configuration allowing your origin. Please check your S3 bucket CORS settings.',
            ),
          );
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      // Don't set Content-Type header - it's already included in the presigned URL signature
      // Setting it manually can cause CORS issues or signature mismatches
      xhr.open('PUT', presignedUrl);
      xhr.send(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      onChange(null);
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSize * 1024 * 1024) {
      const errorMessage = `File size exceeds ${maxSize}MB limit`;
      addToast({
        title: 'Upload Error',
        description: errorMessage,
        color: 'danger',
      });
      handleError(errorMessage);
      updateStage('error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Get MIME type from file extension
    const mimeType = getMimeTypeFromExtension(selectedFile.name) || selectedFile.type || 'application/octet-stream';

    // Reset state
    setError(null);
    updateProgress(0);
    updateStage('getting-url');

    try {
      // Generate object key for S3
      const objectKey = `uploads/${uuidv4()}-${selectedFile.name}`;

      // Step 1: Get presigned URL
      const presignedUrlResponse = await filesApi.getPresignedUrl({
        objectKey,
        httpMethod: 'PUT',
        contentType: mimeType,
        expiresIn: 300, // 5 minutes
      });

      const { presignedUrl } = presignedUrlResponse;

      // Step 2: Upload file directly to S3 with progress tracking
      updateStage('uploading');
      updateProgress(0);

      await uploadToS3(selectedFile, presignedUrl, updateProgress);

      // Step 3: Save file metadata to backend
      updateStage('saving-metadata');
      updateProgress(90);

      const fileMetadata = await filesApi.saveFileMetadata({
        name: selectedFile.name,
        url: presignedUrlResponse.fileUrl,
        size: selectedFile.size,
        type: mimeType,
        provider: 's3',
        isMediaLibrary,
      });

      updateProgress(100);
      updateStage('completed');

      // Update the file state
      onChange(selectedFile);

      // Call the callback with file metadata
      if (onFileUploaded) {
        onFileUploaded(fileMetadata);
      }

      addToast({
        title: 'Upload Successful',
        description: 'File uploaded successfully',
        color: 'success',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload file';
      console.error('File upload error:', err);
      addToast({
        title: 'Upload Error',
        description: errorMessage,
        color: 'danger',
      });
      handleError(errorMessage);
      updateStage('error');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onChange(null);
    }
  };

  const isUploading = ['getting-url', 'uploading', 'saving-metadata'].includes(uploadStage);
  const isCompleted = uploadStage === 'completed';
  const hasError = uploadStage === 'error';

  return (
    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptStringForInput(acceptedTypes)}
        onChange={handleFileChange}
        className="hidden"
        id="file-upload"
        disabled={isUploading}
      />
      <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center space-y-2">
        <Upload className="w-8 h-8 text-gray-400" />
        <div className="w-full">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {file ? file.name : t('guides.modal.form.file.placeholder', { type: acceptedTypes.join(', ') })}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('guides.modal.form.file.description', { type: acceptedTypes.join(', '), maxSize })}
          </p>
        </div>
      </label>

      {/* Progress Bar */}
      {isUploading && (
        <div className="mt-4 space-y-2">
          <Progress
            aria-label="Upload progress"
            value={uploadProgress}
            className="w-full"
            color="primary"
            showValueLabel
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {uploadStage === 'getting-url' && 'Getting upload URL...'}
            {uploadStage === 'uploading' && 'Uploading to S3...'}
            {uploadStage === 'saving-metadata' && 'Saving file metadata...'}
          </p>
        </div>
      )}

      {/* Completed State */}
      {isCompleted && file && (
        <div className="mt-4 flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">Upload complete</span>
        </div>
      )}

      {/* Error State */}
      {hasError && error && (
        <div className="mt-4 text-red-600 dark:text-red-400">
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
